import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import { useCallback, useMemo } from 'react';
import { useEventItem, useMarketItems, useEventItems } from 'modules/common/hooks';
import PrebuiltBetBuilder from 'modules/prebuilt-bets/prebuilt-bet/bet-builder-view/index.js';
import PrebuiltAcca from 'modules/prebuilt-bets/prebuilt-bet/acca-view/index.js';
import PrebuiltCardTypes from 'modules/prebuilt-bets/types/index.js';
import MarketHelper from 'modules/market/helper/market-helper.js';

const { PARTICIPANT_POSITION } = MojitoServices.SportsContent.Events.types;

const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { PREBUILT_ACCA } = PrebuiltCardTypes.MARKETS;

const { competitorIconHooks } = MojitoPresentation.Components;
const { useCompetitorsIconsQuery } = competitorIconHooks;

const { isHomeTeam, isAwayTeam } = MarketHelper;

const PrebuiltBetController = props => {
    const {
        eventId,
        marketId,
        promotionText,
        boostedMarketId,
        imgUrl,
        colorMode,
        shouldRequestData,
        mojitoTools: { config },
    } = props;

    const event = useEventItem(eventId, shouldRequestData);
    const markets = useMarketItems([marketId, boostedMarketId], shouldRequestData);
    const market = markets.find(market => market.id === marketId);
    const boostedMarket = markets.find(market => market.id === boostedMarketId);

    const onEventClick = useCallback(() => {
        if (!event?.sportId || !event?.id) {
            return;
        }
        IntentActionTrigger.showSportEvent(event.sportId, event.id);
    }, [event?.sportId, event?.id]);

    const isAcca = market?.type === PREBUILT_ACCA;
    const showTeamKits = isAcca && config.showTeamKits;
    const { preBuiltBetBuilderParts } = market || {};
    const accaParts = useMemo(
        () => (showTeamKits && preBuiltBetBuilderParts?.length ? preBuiltBetBuilderParts : []),
        [preBuiltBetBuilderParts, showTeamKits]
    );
    const accaEventIds = useMemo(() => accaParts.map(part => part.eventId), [accaParts]);
    const accaMarketIds = useMemo(() => accaParts.map(part => part.marketId), [accaParts]);
    const shouldRequestAccaData = Boolean(shouldRequestData && showTeamKits);
    const accaEvents = useEventItems(accaEventIds, shouldRequestAccaData);
    const accaMarkets = useMarketItems(accaMarketIds, shouldRequestAccaData);

    const teamKitsData = useMemo(() => {
        if (!showTeamKits || !accaEvents?.length || !accaMarkets?.length) {
            return [];
        }
        return accaParts.map(part => {
            const event = accaEvents.find(event => +event.id === +part.eventId);
            const sportId = event?.sportId;
            const participants = event?.participants || [];
            const accaMarket = accaMarkets.find(market => +market.id === +part.marketId);
            const selection = accaMarket?.selections.find(selection => +selection.id === +part.id);
            const playerId = accaMarket?.playerInfo?.id;

            if (playerId) {
                const playerInfo = event?.playersInfo?.[playerId];
                return playerInfo ? { ...playerInfo, sportId, selectionId: selection?.id } : {};
            }

            let participant;
            if (selection && isHomeTeam(selection)) {
                participant = participants.find(p => p.position === PARTICIPANT_POSITION.HOME);
            } else if (selection && isAwayTeam(selection)) {
                participant = participants.find(p => p.position === PARTICIPANT_POSITION.AWAY);
            }

            return participant ? { ...participant, sportId, selectionId: selection?.id } : {};
        });
    }, [showTeamKits, accaEvents, accaMarkets, accaParts]);

    const { competitorsWithIcons } = useCompetitorsIconsQuery({
        competitors: teamKitsData,
        config: config.competitorIconOptions,
    });

    const iconsMap = useMemo(() => {
        const result = {};
        accaParts.forEach(part => {
            competitorsWithIcons.forEach(competitor => {
                if (competitor.selectionId === part.id) {
                    result[part.id] = competitor.iconSrc;
                }
            });
        });

        return result;
    }, [competitorsWithIcons, accaParts]);

    if (!market || !event) {
        return null;
    }

    if (isAcca) {
        return (
            <PrebuiltAcca
                config={config.prebuiltAcca}
                onEventClick={onEventClick}
                market={market}
                event={event}
                promotionText={promotionText}
                imgUrl={imgUrl}
                colorMode={colorMode}
                icons={iconsMap}
                shouldRequestData={shouldRequestData}
            />
        );
    }

    return (
        <PrebuiltBetBuilder
            config={config.prebuiltBetBuilder}
            onEventClick={onEventClick}
            market={market}
            boostedMarket={boostedMarket}
            event={event}
            promotionText={promotionText}
            imgUrl={imgUrl}
            colorMode={colorMode}
            shouldRequestData={shouldRequestData}
        />
    );
};

export default PrebuiltBetController;
